import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const TermOfUseForAttendees = () => {
  const { currentLang } = useLanguage();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', title: 'Introduction' },
    { id: 'who-we-are', title: '1. Who We Are & What We Do' },
    { id: 'purchasing', title: '2. Purchasing Tickets & Registrations' },
    { id: 'payment', title: '3. Payment' },
    { id: 'cancelled', title: '4. Cancelled, Postponed or Varied Events' },
    { id: 'exchanges', title: '5. Exchanges & Refunds' },
    { id: 'unauthorised', title: '6. Unauthorised Use' },
    { id: 'permitted', title: '7. Permitted Use of the Site' },
    { id: 'breaches', title: '8. Breaches of Terms' },
    { id: 'disclaimer', title: '9. Disclaimer & Limitation of Liability' },
    { id: 'indemnity', title: '10. Indemnity' },
    { id: 'general', title: '11. General Provisions' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset for header navbar
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100; // Offset for header navbar
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 antialiased selection:bg-indigo-100 selection:text-indigo-900 font-sans">
      <Navbar />
      
      {/* Header Banner */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden font-sans">
        {/* Glow element */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/15 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              Attendee Terms of Use
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto font-sans">
              Terms and conditions for ticket buyers and event registrants on BookMeIn.Events.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 font-semibold font-sans">
              <SafeIcon name="Clock" className="w-4 h-4 text-indigo-400" />
              Last updated: August 2026
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-1/4">
            <div className="sticky top-28 bg-white/75 backdrop-blur-md border border-slate-200/80 p-6 rounded-2xl shadow-sm hidden lg:block">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                <SafeIcon name="Compass" className="w-4 h-4 text-indigo-500" />
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {sections.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all duration-200 block ${
                      activeSection === sec.id
                        ? 'bg-indigo-50 text-indigo-600 border-l-2 border-indigo-600'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'
                    }`}
                  >
                    {sec.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Terms Text Content */}
          <article className="lg:w-3/4 bg-white border border-slate-200/80 rounded-3xl p-6 sm:p-10 shadow-sm leading-relaxed text-slate-700 font-sans">
            
            {/* Introduction */}
            <div id="intro" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100 flex items-center gap-2">
                <SafeIcon name="FileText" className="w-5 h-5 text-indigo-500" />
                Introduction
              </h2>
              <div className="space-y-4">
                <p>
                  This website (<strong>&ldquo;Site&rdquo;</strong>) is owned and operated by <strong>BMI SOFTWARE S.A.</strong> (BRN 166543403000) trading as <strong>&ldquo;BookMeIn.Events&rdquo;</strong>. Throughout these Terms of Use, the terms <strong>&ldquo;BookMeIn.Events&rdquo;</strong>, <strong>&ldquo;we&rdquo;</strong>, <strong>&ldquo;us&rdquo;</strong> and <strong>&ldquo;our&rdquo;</strong> refer to BMI SOFTWARE S.A. and its associated entities and <strong>&ldquo;you&rdquo;</strong> refers to the individual accessing the Site.
                </p>
                <p>
                  These terms and conditions govern the relationship between you and BookMeIn.Events. There may be additional terms and conditions imposed by an event organizer or venue operator in respect of an event for which you purchase tickets. It is your responsibility to review these terms prior to the purchase of tickets.
                </p>
                <p>
                  Please read these terms of use carefully. By visiting the Site and purchasing tickets and/or registrations you are agreeing to be bound by the following terms (<strong>&ldquo;Terms&rdquo;</strong>) and by the BookMeIn.Events&rsquo; Term of Use. You must not use or access the Site or deal with the tickets you purchase in violation of these Terms. If you choose not to be bound by the Terms then you must not use the Site or purchase tickets from the Site.
                </p>
                <p>
                  BMI SOFTWARE S.A. may vary these Terms at any time and will post the variations on this Site. By continuing to use the Site after the varied terms are posted, you agree to be bound by the variation.
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <div id="who-we-are" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                1. Who We Are and What We Do
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.1.</span>
                  <span>BMI SOFTWARE S.A. facilitates the sale of tickets and membership registrations by Ticket Sellers by allowing Ticket Sellers to create an event page on the Site from which tickets or registrations can be purchased by you (&ldquo;event page&rdquo;) and giving Ticket Sellers access to a secure payment gateway. As such, except as specified in these Terms, we are not liable for any dispute over an event or a ticket purchase.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.2.</span>
                  <span>Details of the event organizer can be found on each event page. Each ticket is issued subject to the terms and conditions of the event organizer and/or the venue at which the event is held.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.3.</span>
                  <span>We have no control over ticket prices, postage and handling fees or ticket delivery methods.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">1.4.</span>
                  <span>You acknowledge that BMI SOFTWARE S.A. acts as an agent for the relevant event organizer. Any claims with respect to the purchase of tickets or membership registrations (including cancellation or postponement of events) lie solely with the event organizer and to the extent permitted by law BMI SOFTWARE S.A. takes no responsibility or liability for such claims.</span>
                </li>
              </ul>
            </div>

            {/* Section 2 */}
            <div id="purchasing" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                2. Purchasing Tickets or Registrations
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.1.</span>
                  <span>When you make a purchase from the Site you acknowledge and agree that:</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.2.</span>
                  <span>We reserve the right to cancel any ticket and refund the amount paid in full where there has been an error in ticket pricing, whether due to human error or a transactional malfunction of the Site, and offer to rectify such an error by re-processing the transaction with the correct ticket pricing.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.3.</span>
                  <span>The event organizer has the right to add or vary the advertised program, seating arrangements and audience capacity and deny admission with reasonable cause including due to intoxication, unsociable behavior, failing to observe lawful directions, possession of invalid or insufficient tickets or ID or health and safety precautions.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">2.4.</span>
                  <span>You are responsible for having your tickets, as purchased through BookMeIn.Events, available at the event. No refunds or exchanges are provided for lost or stolen tickets or for a failure on your part to present at the event. It is your responsibility to provide correct e-ticket information (email address) to allow adequate time for the delivery of tickets.</span>
                </li>
              </ul>
            </div>

            {/* Section 3 */}
            <div id="payment" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                3. Payment
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.1.</span>
                  <span>All credit card purchases are processed through our secure online payment system. The event organizer partially determines how you may pay for tickets and registrations based on the selected payment gateway.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.2.</span>
                  <span>Tickets purchased on the Site are subject to a non-refundable per order service fee which will be included in the final price.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.3.</span>
                  <span>Unless stated to be otherwise, charges referred to for any tickets or registrations supplied via the Site are stated inclusive of VAT or GST. A tax invoice will be provided to you upon completing your purchase of tickets.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">3.4.</span>
                  <span>All prices are stated in Euro, US and Australian dollars.</span>
                </li>
              </ul>
            </div>

            {/* Section 4 */}
            <div id="cancelled" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                4. Cancelled, Postponed or Varied Events
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">4.1.</span>
                  <span>The event organizer is solely responsible and liable for providing any refund or comparable ticket where an event is cancelled, varied or postponed. You should check the terms of sale of the Ticket Seller with respect to its policy on cancelled, varied or postponed events.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">4.2.</span>
                  <span>So that the event organizer can contact you with information relating to your tickets or the event, we may be required to pass on your contact information. We do not authorize the event organizer to send you any unsolicited emails, mail or &ldquo;spam&rdquo;.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">4.3.</span>
                  <span>We and the event organizer will use reasonable endeavors to notify ticket holders of a cancellation, variation or postponement; however, the responsibility to ascertain whether an event has been cancelled, varied or postponed is yours. We do not guarantee that the ticket holders will be informed of a cancellation, variation or postponement before the date of the event.</span>
                </li>
              </ul>
            </div>

            {/* Section 5 */}
            <div id="exchanges" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                5. Exchanges and Refunds
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.1.</span>
                  <span>All ticket sales are final. For clarification, you are not entitled to a refund if you change your mind or cannot attend.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">5.2.</span>
                  <span>If your ticket is lost, stolen or damaged (including if it is lost in the mail), we are not required to replace it unless we can identify the seat corresponding to the ticket, or in the case of a general admission ticket can identify and cancel the relevant ticket. We may charge a reasonable administration fee in these circumstances.</span>
                </li>
              </ul>
            </div>

            {/* Section 6 */}
            <div id="unauthorised" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                6. Unauthorised Use
              </h2>
              <p className="mb-4">
                The following are examples of unauthorised use of the Site and therefore such actions constitute a breach of these Terms. You agree that you will not:
              </p>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.1.</span>
                  <span>facilitate illegal ticket sales or use the Site to resell tickets of any kind, including by way of unauthorised framing or links to other sites for the purpose of reselling;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.2.</span>
                  <span>interfere with the proper working of the Site or take any action that imposes an unreasonably large burden on the Site;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.3.</span>
                  <span>request more than 10,000 pages of the Site in any twenty-four hour period or make any other request to transactional servers more than once during any three second interval (this applies equally to a group of individuals acting in concert); or</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">6.4.</span>
                  <span>hack into the Site or utilise automated means to process or place ticket orders including the use of robots, spiders or other devices to process ticket sales.</span>
                </li>
              </ul>
            </div>

            {/* Section 7 */}
            <div id="permitted" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                7. Permitted Use of the Site
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.1.</span>
                  <span>You may use the Site for your personal use only. You may make a temporary copy of all or part of the Site on your local computer for the sole purpose of viewing it. You may also retain a single permanent copy of a part of the Site for your personal use (including to purchase tickets). You may not otherwise in any form copy, reproduce, modify, create derivative works, publish or distribute the material on the Site without the prior written consent of BMI SOFTWARE S.A. or as permitted by applicable copyright laws.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.2.</span>
                  <span>All content, images, software and designs on the Site (&ldquo;Content&rdquo;) are owned by us or used under license or with consent, including from event organizers. The Content is protected by copyright under the laws of Australia and other countries through international treaties. You must not remove any proprietary notice or credits contained on the Site.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.3.</span>
                  <span>The Site contains trade marks, logos and trade names of BookMeIn.Events, which may be registered or otherwise protected by law. You must not use any trade marks, logos or trade names which are used on the Site. Nothing contained on this Site should be construed as granting any license or right to use any trade mark displayed on the Site.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">7.4.</span>
                  <span>We reserve all of our rights to enforce our intellectual property rights to the fullest extent of the law.</span>
                </li>
              </ul>
            </div>

            {/* Section 8 */}
            <div id="breaches" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                8. Breaches of Terms
              </h2>
              <p className="mb-4">
                If you breach these Terms or we believe you are or were acting in concert with any person who has done so, we may without prior notice:
              </p>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.1.</span>
                  <span>block or limit your access to the Site;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.2.</span>
                  <span>cancel any tickets, registrations or ticket orders you have placed via the Site without refund;</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.3.</span>
                  <span>take legal action against you; and/or</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">8.4.</span>
                  <span>prohibit you from future ticket or registration purchases.</span>
                </li>
              </ul>
            </div>

            {/* Section 9 */}
            <div id="disclaimer" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                9. Disclaimer and Limitation of Liability
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.1.</span>
                  <span>You use the Site at your own risk.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.2.</span>
                  <span>We do not guarantee, represent or warrant that the Site will be free of defects or bugs, or that your access will be uninterrupted, timely or error-free. Your access to the Site may be suspended without notice in the case of system failure, maintenance or repair or any reason beyond our control.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.3.</span>
                  <span>We reserve the right to change or discontinue any feature or service on the Site at any time.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.4.</span>
                  <span>To the extent permitted by law, we exclude all conditions and warranties relating to your use of the Site that are not set out in these Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.5.</span>
                  <span>The goods and services provided by BookMeIn.Events come with certain guarantees that cannot be excluded under the European Consumer Law. Our liability for any breach of those guarantees is limited to the replacement of goods or re-supply of services or the replacement cost of goods or re-supply of services. As agent for the event organizer, our services to you are solely the facilitation of ticket sales.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.6.</span>
                  <span>In relation to any express warranty or condition set out in these Terms in connection with goods or services supplied or offered by us via the Site, our liability to you will be limited to the amount(s) paid by you (if any) in respect of those goods or services.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">9.7.</span>
                  <span>BMI SOFTWARE S.A. disclaims any and all liability for the acts, omissions and conduct of any event organizers, third party users, promoters, advertisers and/or sponsors on the Site or otherwise related to your use of the Site. We are not responsible for the products, services, actions or failure to act of any venue, performer, promoter or other third party in connection with or referenced on the Site.</span>
                </li>
              </ul>
            </div>

            {/* Section 10 */}
            <div id="indemnity" className="scroll-mt-28 mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                10. Indemnity
              </h2>
              <p>
                You agree to fully indemnify and hold us harmless against any expenses, costs, loss (including consequential loss) or damage that we may suffer or incur as a result of or in connection with your use of, or access to or conduct in connection with the Site, including any breach by you of these Terms.
              </p>
            </div>

            {/* Section 11 */}
            <div id="general" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
                11. General Provisions
              </h2>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">11.1.</span>
                  <span>The Terms shall in all respects be governed by the laws of Greece, Europe. You consent to the exclusive jurisdiction of the courts in Victoria to determine any matter or dispute which arises under the User Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">11.2.</span>
                  <span>If any provision of the Terms is deemed invalid or unenforceable, all or part of that provision will be severed from the Terms and will not affect the enforceability of the remaining provisions of the Terms.</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-semibold text-indigo-600 shrink-0">11.3.</span>
                  <span>No waiver of any term of the Terms shall be deemed a further or continuing waiver of such term or any other term. Any failure to assert any right under the Terms shall not constitute a waiver of such right.</span>
                </li>
              </ul>
            </div>

          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermOfUseForAttendees;
